import _ from "lodash";
import React, { memo, useCallback, useMemo } from "react";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import StandardsTable from "StandardsTable";
import useStandardsTableProps from "StandardsTable/hooks/useStandardsTableProps";
import { getColumnIdsToDisplay } from "../../modules/Utils";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import ACLStore from "lib/aclStore";
import { ENABLE_PARENT_STANDARDS_TAGGING } from "Constants/permissionConstants";

const COLUMN_PROPERTIES = {
  [DATA_GRID_COLUMN_TYPES.GRADE_LEVELS]: {
    customConfig: {
      property: "grades",
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGS]: {
    customConfig: {
      property: "standardsWrapper.properties.tags",
      showEmptyCellAsFallback: true,
    },
  },
};

const _getEdgesFromNodeMap = ({
  nodeMap,
  selectedStandards,
  isParentTaggingEnabled,
}) => {
  return Array.from(nodeMap.values(), ({ rowId, children, columns }) => {
    if (!isParentTaggingEnabled && !_.isEmpty(children))
      columns[0].isDisabled = true;

    columns[0].value = getCheckboxValue({
      value: selectedStandards.has(rowId),
    });

    return {
      rowId,
      children,
      columns,
    };
  });
};

const Standards = props => {
  const {
    selectedDataGridRows,
    handleDataGridRowsUpdate,
    plannerElementType,
    flattenedNestedNodes,
  } = props;

  const defaultColumns = useMemo(() => {
    return getColumnIdsToDisplay({
      plannerElementType,
    });
  }, [plannerElementType]);

  const edges = useMemo(() => {
    return _.map(flattenedNestedNodes, edge => ({
      node: {
        ...edge,
        parentId: edge.parent,
      },
    }));
  }, [flattenedNestedNodes]);

  const { columns, nodeMap, properties } = useStandardsTableProps({
    columns: defaultColumns,
    edges,
    properties: COLUMN_PROPERTIES,
  });

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectedStandards: new Set(selectedDataGridRows),
        isParentTaggingEnabled: ACLStore.can(ENABLE_PARENT_STANDARDS_TAGGING),
      });
    },
    [selectedDataGridRows]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      handleSelectionChange({
        params,
        selectedRows: selectedDataGridRows,
        setSelectedRows: rows => handleDataGridRowsUpdate(Array.from(rows)),
      });
    },
    [handleDataGridRowsUpdate, selectedDataGridRows]
  );

  return (
    <StandardsTable columns={columns} nodeMap={nodeMap} properties={properties}>
      <StandardsTable.Body>
        <StandardsTable.Grid
          freezeColumns={2}
          getEdgesFromNodeMap={getEdgesFromNodeMap}
          onCellEdit={onCellEdit}
        />
      </StandardsTable.Body>
    </StandardsTable>
  );
};

export default memo(Standards);
